import {
  Injectable,
  signal,
  WritableSignal,
  inject,
  ModelSignal,
  effect,
  untracked,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { Program } from '../ast/ast-node.model';
import { ProgramNode } from '../ast/program';
import { TemplateVariable } from '../interfaces/template-variable.interface';
import { templateEditorLogger } from '../logger.instance';
import { AstParserService } from './ast-parser.service';
import { CursorManagementService } from './cursor-management.service';
import { DomRendererService } from './dom-renderer.service';
import { ElementInsertionService } from './element-insertion.service';
import { UndoRedoService } from './undo-redo.service';

const logger = templateEditorLogger.extend('service');

export interface TemplateEditorSettings {
  noLineBreaks: boolean;
  readOnly: boolean;
}

/**
 * Serviço principal que orquestra o editor de template.
 */
@Injectable()
export class TemplateEditorService {
  public inputValue: WritableSignal<string> = signal('');

  public knownVariables = signal<Map<string, TemplateVariable>>(new Map());

  private valueSignal: ModelSignal<string> | null = null;

  private settings = signal<TemplateEditorSettings>({
    readOnly: false,
    noLineBreaks: false,
  });

  private undoRedoService = inject(UndoRedoService);

  private astParser = inject(AstParserService);

  private domRenderer = inject(DomRendererService);

  private cursorService = inject(CursorManagementService);

  private insertionService = inject(ElementInsertionService);

  private editorElement!: HTMLDivElement;

  private isRestoringState = false;

  private saveStateSubject$ = new Subject<void>();

  private ast: Program = new ProgramNode([]);

  public constructor() {
    effect(() => {
      const currentValue = this.inputValue();
      if (this.valueSignal) {
        untracked(() => {
          this.valueSignal!.set(currentValue);
        });
      }
    });

    this.saveStateSubject$.pipe(debounceTime(500)).subscribe(() => this.saveState());
  }

  public initialize(
    editorElement: HTMLDivElement,
    initialVariables: TemplateVariable[],
    valueSignal: ModelSignal<string>,
    settings?: Partial<TemplateEditorSettings>,
  ): void {
    logger.debug('Initializing TemplateEditorService', {
      initialVariables,
      value: valueSignal(),
      settings,
    });
    this.editorElement = editorElement;
    this.updateKnownVariables(initialVariables);
    this.valueSignal = valueSignal;
    this.inputValue.set(valueSignal());
    if (valueSignal()) {
      this.renderInitialContent();
    }

    this.inputValue.set(this.parseEditorContent());
    this.saveState();
    if (settings) {
      this.settings.update((current) => ({ ...current, ...settings }));
    }
  }

  public updateKnownVariables(vars: TemplateVariable[]): void {
    this.knownVariables.update((currentMap) => {
      for (const variable of vars) currentMap.set(variable.path, variable);
      return new Map(currentMap);
    });
  }

  public getTextBeforeCursor(container: Node, range: Range): string {
    return this.cursorService.getTextBeforeCursor(container, range);
  }

  public updateRichEditorFromText(text: string): void {
    logger.debug('updateRichEditorFromText', { text });
    this.ast = this.astParser.parseStringToAst(text);
    const html = this.domRenderer.renderAstToHtml(this.ast, this.knownVariables());
    this.editorElement.innerHTML = html;
    this.saveState();
  }

  private renderInitialContent(): void {
    const ast = this.astParser.parseStringToAst(this.inputValue());
    this.editorElement.innerHTML = this.domRenderer.renderAstToHtml(ast, this.knownVariables());
  }

  public parseEditorContent(): string {
    const ast = this.domRenderer.buildAstFromDom(this.editorElement, this.knownVariables());
    const text = this.astParser.stringifyAst(ast).trim();
    logger.debug('parseEditorContent', { text, ast });
    this.ast = ast;
    return text;
  }

  public insertBlock(variable: TemplateVariable, term: string, trigger: { char: string }): void {
    logger.debug('insertBlock', { variable, term, trigger });
    this.insertionService.insertBlock(
      variable,
      term,
      trigger,
      this.editorElement,
      this.knownVariables(),
    );
  }

  public insertMustache(variable: TemplateVariable, term: string, trigger: { char: string }): void {
    logger.debug('insertMustache', { variable, term, trigger });
    this.insertionService.insertMustache(variable, term, trigger);
    this.updateKnownVariables([variable]);
  }

  public insertVariableIntoArgument(
    variable: TemplateVariable,
    argSpan: HTMLElement,
    term: string,
    trigger: { char: string },
  ): void {
    this.insertionService.insertVariableIntoArgument(variable, argSpan, term, trigger);
  }

  public deleteElement(element: HTMLElement): void {
    if (this.insertionService.deleteElement(element, this.editorElement)) {
      this.inputValue.set(this.parseEditorContent());
      this.saveState();
    }
  }

  public handleBackspace(event: KeyboardEvent, onModified: () => void): void {
    this.cursorService.handleBackspace(event, this.editorElement, onModified);
  }

  public saveState(): void {
    if (this.isRestoringState) return;
    this.undoRedoService.saveState(this.editorElement.innerHTML);
  }

  public undo(): void {
    const prevState = this.undoRedoService.undo();
    logger.debug('undo', { hasPrevState: prevState !== null });
    if (prevState !== null) {
      this.restoreState(prevState);
    }
  }

  public redo(): void {
    const nextState = this.undoRedoService.redo();
    logger.debug('redo', { hasNextState: nextState !== null });
    if (nextState !== null) {
      this.restoreState(nextState);
    }
  }

  public scheduleSaveState(): void {
    this.saveStateSubject$.next();
  }

  private restoreState(state: string): void {
    logger.debug('restoreState', { state });
    this.isRestoringState = true;
    this.editorElement.innerHTML = state;
    this.inputValue.set(this.parseEditorContent());
    this.cursorService.setCursorAtEnd(this.editorElement);
    this.isRestoringState = false;
  }
}
